import * as React from 'react';
import PropertyLabel from '../PropertyLabel/PropertyLabel';
import {
  NamedPopoverProps,
  DeprecatedPopoverBridge,
} from '@packages/systems/design-system/PopoverBridge';
import {weakMemo} from '@packages/utilities/memo';
import {tokens} from '@packages/systems/spring';
import {SettingsPanelPropertyMenu} from '../SettingsPanelPropertyMenu';
import {useExpressionEditorImplementationContext} from '@packages/systems/ExpressionEditor-utils';
import {type LabelItemResult} from '@packages/systems/ExpressionEditor-custom-controls';
import type {Box} from '@packages/utilities/box';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

type MakePopoverRendererFirstArgsType = {
  items: Array<LabelItemResult>;
};
type MakePopoverRendererSecondArgsType = {
  closePopover: () => void;
  viewport: Box;
};
type MakePopoverRendererFnType = (
  arg1: MakePopoverRendererFirstArgsType
) => (arg1: MakePopoverRendererSecondArgsType) => React.ReactElement;
const noop = () => {};

const makePopoverRenderer: MakePopoverRendererFnType = weakMemo(
  ({items}: MakePopoverRendererFirstArgsType) =>
    ({closePopover, viewport}: MakePopoverRendererSecondArgsType) => {
      return (
        <SettingsPanelPropertyMenu
          style={{
            ...(viewport
              ? {
                  // HACKish: The -2 here is to accomodate for a likely 1px border;
                  // ideally our viewport would be aware of this:
                  maxWidth: viewport.width - 2,
                  minWidth: viewport.width - 40,
                  maxHeight: viewport.height - 2,
                }
              : null),
          }}
          items={items}
          closePopover={closePopover}
        />
      );
    }
);

type SettingsPanelPropertyLabelProps = {
  children: React.ReactNode;
  labelWidth?: 'default' | 'narrow' | 'narrowest';
  items: Array<LabelItemResult>;
  ['data-automation-id']?: string;
  hover?: boolean;
  style?: CSSProperties;
};

const baseStyle = {
  whiteSpace: 'normal',
} as const;

const newOverrideStyle = {
  ...baseStyle,
  padding: `0 ${tokens.space[0.25]}`,
} as const;

const newOverrideDefaultStyle = {
  ...newOverrideStyle,
  color: tokens.colors.textPrimary,
} as const;

const withNarrowerLabel = {
  fontSize: 11,
  letterSpacing: '-0.4px',
} as const;

const withNarrowestLabel = {
  fontSize: 10,
  letterSpacing: '-0.4px',
} as const;

export const SettingsPanelPropertyLabel = ({
  labelWidth = 'default',
  ...props
}: SettingsPanelPropertyLabelProps) => {
  const implementationContext = useExpressionEditorImplementationContext();
  const context = implementationContext?.env?.context;
  const PopoverWithoutMask = React.useMemo(
    () => (_props: NamedPopoverProps) => (
      <DeprecatedPopoverBridge
        metricsIdentifier="settings-panel-property-label-popover"
        {..._props}
        showMask="TRANSPARENT"
      />
    ),
    []
  );
  const isDefault = props.items.length === 0;

  let style =
    context === 'override'
      ? isDefault
        ? newOverrideDefaultStyle
        : newOverrideStyle
      : baseStyle;

  if (labelWidth === 'narrow') {
    style = {
      ...style,
      ...withNarrowerLabel,
    };
  }

  if (labelWidth === 'narrowest') {
    style = {
      ...style,
      ...withNarrowestLabel,
    };
  }

  const hasWarning = props.items.some((item) => item.hasWarning);

  const origin = isDefault
    ? {type: 'default', rule: null}
    : {
        type: 'local',
        rule: null,
        hasWarning,
      };

  const singleItemClickable =
    props.items.length === 1 ? props.items[0]?.clickable : undefined;
  const onResetValueClick = singleItemClickable?.onClick ?? noop;
  // Nothing to reset, but the popover explains why — show it instead of
  // swallowing the alt-click.
  const altClickOpensPopover = props.items.length === 1 && !singleItemClickable;

  return (
    <PropertyLabel
      {...props}
      renderPopoverContent={makePopoverRenderer(props)}
      Popover={PopoverWithoutMask}
      // @ts-expect-error - TS2322 - Type '{ type: string; rule: null; hasWarning?: undefined; } | { type: string; rule: null; hasWarning: boolean; }' is not assignable to type 'PropertyOrigin'.
      origin={origin}
      onResetValueClick={onResetValueClick}
      altClickOpensPopover={altClickOpensPopover}
      style={props.style ? {...props.style, ...style} : style}
    >
      {props.children}
    </PropertyLabel>
  );
};
